import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useScrollMotion from '../../hooks/useScrollMotion';
import { CONTACT } from '../../config/contact';
import HygieneCompare from '../../components/HygieneCompare/HygieneCompare';
import {
  BoltIcon,
  CardIcon,
  ClockIcon,
  DropIcon,
  LeafIcon,
  QrIcon,
  SavingsIcon,
  StarIcon,
} from '../../components/Icons/Icons';
import m from '../../styles/marketing.module.css';
import styles from './Home.module.css';

/** Icons for the eco card's two points, in copy order. */
const ECO_ICONS = [DropIcon, BoltIcon];


/* Photo for each local-discounts card, in copy order. The tiles carry no type
   of their own — the section's own head and lead do the talking, so these are
   photographs rather than captioned cards. */
const DISCOUNT_CARDS = [
  'cafe.jpg',
  'conv-store.jpeg',
  'night-market.jpg',
  'shopping.jpeg',
];

/* App screenshot and label icon for each mobile-payment card, in copy order.
   The first three are the three ways to pay the section argues for — a QR rail,
   a card, and value already loaded on the wallet — in that order, because that
   is the order a Thai customer reaches for them. All four are portrait phone
   captures of different native sizes, so the card crops them to one window
   rather than laying them out by their own aspect. */
const PAYMENT_SCREENS = [
  { file: 'bank-pay.png', Icon: QrIcon },
  { file: 'screen-pay.png', Icon: CardIcon },
  { file: 'wallet.png', Icon: SavingsIcon },
  { file: 'add_time.png', Icon: ClockIcon },
];

/* -------------------------------------------------------------------------
   Shapes of the list-valued translation keys.
   ---------------------------------------------------------------------- */

interface Stat { value: string; caption: string }
interface EngPoint { lead: string; body: string }
interface EngStat { value: string; label: string }
interface DiscountCard { title: string; imageAlt: string }
interface PaymentScreen { label: string; imageAlt: string }

const Home = () => {
  const { t } = useTranslation('home');
  const root = useScrollMotion<HTMLDivElement>();

  const list = <T,>(key: string) =>
    t(key, { returnObjects: true }) as unknown as T[];

  const stats = list<Stat>('stats.items');
  const ticker = list<string>('ticker');
  const ecoPoints = list<string>('why.eco.points');
  const engPoints = list<EngPoint>('engineering.points');
  const engStats = list<EngStat>('engineering.stats');
  const wallAlts = list<string>('wall.images');
  const pairAlts = list<string>('sizeMatters.pairAlts');

  /**
   * A section's chapter word. The scroll layer slides it in from off-side, so
   * it carries no CSS animation of its own — the two would fight over the
   * inline transform.
   */
  const kinetic = (key: string, dir: 'left' | 'right', light = false) => (
    <div
      className={light ? `${m.kinetic} ${m.kineticLight}` : m.kinetic}
      data-scrub={dir}
    >
      <Trans i18nKey={key} ns="home" components={{ hl: <span /> }} />
    </div>
  );

  const tickerRow = (
    <div className={styles.tickerRow}>
      {ticker.map((item) => (
        <Fragment key={item}>
          <span>{item}</span>
          <span className={styles.tickerDot}>●</span>
        </Fragment>
      ))}
    </div>
  );

  return (
    <div ref={root}>
      {/* 1. Hero — one question on a light blue field, then the screen is handed
           to the product. No CTA row, feature line or image: all three were
           intentionally dropped in the "top sections" revision. */}
      <header className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={`${styles.heroEyebrow} ${styles.rise}`}>{t('hero.eyebrow')}</div>
          {/* A paragraph, not an h1 — it is display type carrying a question,
              and the page's real heading hierarchy starts at the sections. */}
          <p className={`${styles.heroHeadline} ${styles.rise}`} data-d="1">
            <Trans i18nKey="hero.headline" ns="home" components={{ hl: <span /> }} />
          </p>
        </div>
      </header>

      {/* 1b. Brand moment — the card pins for a screen's worth of scroll while
           "scan" and "wash." scrub in. Driven by the page's single scroll
           subscription in useScrollMotion, writing CSS custom properties. */}
      <section className={styles.bm} aria-label={t('brandMoment.label')}>
        <div className={styles.bmTrack} data-bm-track>
          <div className={styles.bmStage}>
            <div className={styles.bmCard} data-bm-card>
              <img src="/images/stackcombo.webp" alt={t('brandMoment.imageAlt')} />
              <div className={`${styles.bmWord} ${styles.bmWordA}`} data-bm-word="a">
                {t('brandMoment.wordA')}
              </div>
              <div className={`${styles.bmWord} ${styles.bmWordB}`} data-bm-word="b">
                {t('brandMoment.wordB')}
              </div>
              {/* The brand tagline, sat under the machine it describes. */}
              <div className={styles.bmCaption}>
                <Trans i18nKey="brandMoment.caption" ns="home" components={{ hl: <span /> }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Stat band */}
      <section className={styles.statBand}>
        <div className={m.container}>
          <div className={styles.statEyebrow}>{t('stats.eyebrow')}</div>
          <div className={styles.statGrid}>
            {stats.map((stat) => (
              <div key={stat.value}>
                <div className={styles.statNumber}>{stat.value}</div>
                <div className={styles.statCaption}>{stat.caption}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Ticker — the track is the row twice over, so the -50% loop is seamless */}
      <div className={styles.ticker} aria-hidden="true">
        <div className={styles.tickerTrack}>
          {tickerRow}
          {tickerRow}
        </div>
      </div>

      {/* 4. 23kg capacity. First of the six tenant chapters, and where the nav's
           "Tenants" link lands.

           An intro block over a full-width photo band — the shape the payments
           chapter uses. It was a two-column story grid, which needs a full copy
           column to balance it; once the eyebrow, the drum paragraph and the
           savings note collapsed into a single headline, the copy ran a third
           the height of the media stack and left the lower-left quarter of the
           section empty. */}
      <section className={m.section} id="tenants">
        <div className={m.container}>
          {kinetic('sizeMatters.kinetic', 'left')}
          <div className={styles.capacityIntro}>
            <h2 className={m.columnHead}>{t('sizeMatters.head')}</h2>
            <a href="#engineering" className={`${m.btn} ${m.btnMd} ${m.btnOutline}`}>
              {t('sizeMatters.cta')}
            </a>
          </div>
          <div className={styles.capacityBand} data-reveal>
            {/* The chip is anchored to this frame rather than to the band, so it
                tracks the photo it belongs to. */}
            <div className={styles.capacityFrame}>
              <img
                className={styles.capacityImg}
                data-parallax="0.05"
                src="/images/looking-washer.jpeg"
                alt={t('sizeMatters.imageAlt')}
              />
              <div className={`${m.chip} ${styles.capacityChip}`}>
                <div className={m.chipBadge}>{t('sizeMatters.chip.badge')}</div>
                <div>
                  <div className={m.chipTitle}>{t('sizeMatters.chip.title')}</div>
                  <div className={m.chipSub}>{t('sizeMatters.chip.sub')}</div>
                </div>
              </div>
            </div>
            {/* The two things the drum is for: the duvet that goes in whole, and
                the pile that becomes one cycle. */}
            {pairAlts.map((alt, i) => (
              <img
                key={alt}
                className={styles.capacityImg}
                src={`/images/bedding_${i + 1}.jpeg`}
                alt={alt}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 5. Convenient and onsite — the app beside the room it opens.

           Photos across the full container, then the three checkpoints as a row
           beneath. This was copy-left / photos-right, but the eyebrow, heading
           and lead have all gone (the chapter word carries the claim now) and
           three short lines stranded beside two 600px portraits did not earn a
           column. Photos-then-copy is also the inverse of the capacity chapter
           above, so the two picture sections read as a pair, not a repeat.

           The pair is sized by HEIGHT, never by width — see the note on
           --access-h in the stylesheet. */}
      <section className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          {kinetic('wall.kinetic', 'right')}
          <div className={styles.accessPair}>
            <img
              className={styles.accessPhone}
              data-reveal
              src="/images/bed-overhead.jpg"
              alt={wallAlts[0]}
            />
            <div className={styles.accessRoom} data-reveal data-reveal-delay="120">
              <img
                className={styles.accessRoomImg}
                data-parallax="0.04"
                src="/images/24_access.jpeg"
                alt={wallAlts[1]}
              />
              <div className={`${m.chip} ${styles.accessChip}`}>
                <div className={m.chipBadge}>{t('wall.chip.badge')}</div>
                <div>
                  <div className={m.chipTitle}>{t('wall.chip.title')}</div>
                  <div className={m.chipSub}>{t('wall.chip.sub')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* What the app actually does, under the screenshot of it. */}
          <ul className={styles.accessPoints}>
            {list<string>('wall.points').map((point) => (
              <li key={point}><ClockIcon />{point}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* 6-8. Local discounts, mobile payments, loyalty points */}
      {/* 6. Points and discounts — one chapter, not two. Earning and spending are
           the same loop (a wash earns, the points go to merchants around the
           building), and the old loyalty chapter's last point already pointed
           here. The earn half is the text column top-right; the spend half is
           the picture wall below it: four equal photos side by side, carrying
           no type of their own — the copy above has already said it. */}
      <section className={m.section}>
        <div className={m.container}>
          {kinetic('discounts.kinetic', 'left')}
          <div className={styles.discIntro}>
            {/* No head and no lead: the chapter word carries the claim. What is
                left is the label for the chapter and the earn block under it —
                so the copy column that used to sit opposite is gone. */}
            <div className={styles.discEyebrowRow}>
              <div className={m.eyebrow}>{t('discounts.eyebrow')}</div>
              {/* The points programme is OneAsia's, not ours — the badge says
                  so next to the eyebrow rather than in the body copy. */}
              <span className={styles.discPowered}>{t('discounts.powered')}</span>
            </div>
            <div className={styles.discEarn}>
              <div className={styles.discEarnLabel}>{t('discounts.earn.label')}</div>
              <ul className={styles.discEarnList}>
                {list<string>('discounts.earn.points').map((point) => (
                  <li key={point}><StarIcon />{point}</li>
                ))}
              </ul>
              {/* Where the points are redeemable, closing the earn list rather
                  than standing on its own: the list says how they add up, this
                  says where they are good. The mark is a masked shape, not an
                  img, so it takes the section's blue. */}
              <p className={styles.discEarnNote}>
                <span className={styles.discEarnMark} aria-hidden="true" />
                {t('discounts.app.title')}
              </p>
            </div>
          </div>
          <div className={styles.wallGrid}>
            {list<DiscountCard>('discounts.cards').map((card, i) => {
              const file = DISCOUNT_CARDS[i];
              return (
                <article
                  key={card.title}
                  className={styles.wallTile}
                  data-reveal
                  data-reveal-delay={i * 110}
                >
                  <img src={`/images/${file}`} alt={card.imageAlt} />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. Mobile payments — four app screens, one wallet.

           Navy, and the only tenant chapter that is: the subject here is
           software rather than a room, and four bright phone captures only read
           as screens when the page behind them goes dark. It also breaks the
           run — the picture wall above is untinted and the "and more" section
           below is tinted, so white/white/tint became white/navy/tint.

           The cards are deliberately uniform and baseline-aligned. So is the
           picture wall above, but that row is photographs with no type on them
           at all; the navy and the copy under each screen are what keep this
           one from reading as the same section twice.

           Each screenshot is a different native size (566×1274 to 722×1178), so
           the card gives them one 9/16 window and crops from the top — the half
           that carries each screen's headline UI. That keeps the QR whole on the
           first card and leaves PromptPay, the card and Alipay in frame on the
           second, which is the section's whole argument in two crops. The scrim
           over the bottom of the window is what makes the cut read as
           intentional. */}
      <section className={`${m.section} ${m.navy}`}>
        <div className={m.container}>
          {kinetic('payments.kinetic', 'right', true)}
          {/* Chapter word straight to the cards: no heading, no eyebrow and no
              lead. The four cards name the methods themselves, so every line
              that used to sit here — "QR, card or wallet", then "Bank QR,
              Credit Card, and Participating Wallets" — just said them again
              before the reader reached them. The chapter word's own 72px
              bottom margin carries the gap the lead used to provide. */}

          <div className={styles.payGrid}>
            {list<PaymentScreen>('payments.screens').map((screen, i) => {
              const { file, Icon } = PAYMENT_SCREENS[i];
              return (
                <article
                  key={screen.label}
                  className={styles.payCard}
                  data-reveal
                  data-reveal-delay={i * 110}
                >
                  <div className={styles.payScreen}>
                    <img
                      className={styles.payShot}
                      src={`/images/${file}`}
                      alt={screen.imageAlt}
                    />
                  </div>
                  <div className={styles.payLabel}>
                    <span className={styles.payBadge}><Icon /></span>
                    {screen.label}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* 9. Health and hygiene — the mold comparison, then the eco card.

           The chapter word carries the subject, so there is no section heading
           under it: the material-quality statement opens the section directly. */}
      <section className={`${m.section} ${m.tint}`}>
        <div className={m.container}>
          {kinetic('why.kinetic', 'right')}

          {/* Material statement, the mold comparison and the bedding payoff —
              shared with the management page. */}
          <HygieneCompare />

          <div className={styles.whyGrid}>
            <div className={`${styles.whyCard} ${styles.ecoCard}`} data-reveal>
              <div className={styles.ecoPhoto}>
                <img src="/images/eco_2.jpeg" alt={t('why.eco.imageAlt')} />
              </div>
              <div className={styles.whyBody}>
                <div className={styles.ecoHead}>
                  <img src="/images/lh/eco-emblem.png" alt={t('why.eco.emblemAlt')} />
                  <div>
                    <div className={styles.whyPill}>{t('why.eco.pill')}</div>
                    <h3>{t('why.eco.title')}</h3>
                  </div>
                </div>
                <p>{t('why.eco.body')}</p>
                <div className={styles.whyPoints}>
                  {ecoPoints.map((point, i) => {
                    const Icon = ECO_ICONS[i] ?? LeafIcon;
                    return <div key={point}><Icon />{point}</div>;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. Commercial grade */}
      <section className={`${m.section} ${m.navy}`} id="engineering">
        <div className={m.container}>
          {kinetic('engineering.kinetic', 'left', true)}
          <div className={styles.engHead}>
            <div className={m.eyebrowOnNavy}>{t('engineering.eyebrow')}</div>
            <h2 className={styles.engTitle}>{t('engineering.title')}</h2>
            <p className={styles.engLead}>{t('engineering.lead')}</p>
          </div>
          <div className={styles.engMedia} data-reveal>
            <img src="/images/exploded.jpg" alt={t('engineering.imageAlt')} />
          </div>
          <div className={styles.points}>
            {engPoints.map((point, i) => (
              <div
                key={point.lead}
                className={styles.point}
                data-reveal
                data-reveal-delay={i * 80}
              >
                <div className={styles.pointLead}>{point.lead}</div>
                <p>{point.body}</p>
              </div>
            ))}
          </div>
          <div className={styles.engStats}>
            {engStats.map((stat) => (
              <div key={stat.label}>
                <div className={styles.engStatValue}>{stat.value}</div>
                <div className={styles.engStatLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. Quote */}
      <section className={m.section}>
        <div className={styles.quote} data-reveal>
          <p className={styles.quoteText}>{t('quote.text')}</p>
          <div className={styles.quoteAttr}>{t('quote.attribution')}</div>
        </div>
      </section>

      {/* 12. CTA */}
      <section className={`${m.section} ${m.navy}`} id="contact">
        <div className={styles.cta}>
          <h2 className={styles.ctaTitle}>
            <Trans i18nKey="cta.title" ns="home" components={{ hl: <span /> }} />
          </h2>
          <p className={styles.ctaLead}>{t('cta.lead')}</p>
          <div className={`${m.btnRow} ${m.btnRowCenter}`}>
            <a href={`mailto:${CONTACT.email}`} className={`${m.btn} ${m.btnLg} ${m.btnLight}`}>
              {t('cta.button')}
            </a>
          </div>
          <div className={styles.ctaContact}>
            {CONTACT.phone} &middot; {CONTACT.email} &middot; {CONTACT.website}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
